import React from 'react';
import { Box, Paper, Typography, Button, Chip, Avatar } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { styled } from '@mui/system';
import { Link, useNavigate } from 'react-router-dom';
import {
  ChatMessageEventViewModel,
  MessageEventType,
  CustomerOrderStatus,
  HandymanOrderStatus,
} from '../../configuration/api/apiSchema';
import EventIcon from '@mui/icons-material/Event';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import PaymentIcon from '@mui/icons-material/Payment';
import CancelIcon from '@mui/icons-material/Cancel';
import DoneIcon from '@mui/icons-material/Done';
import StarIcon from '@mui/icons-material/Star';
import { useDispatch } from 'react-redux';
import { updateHandymanProposal } from '../../pages/Orders/actions';

const EventPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(2),
  borderRadius: theme.spacing(3),
  backgroundColor: '#ffffff',
  maxWidth: '70%',
  margin: '0 auto',
  position: 'relative',
  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
  border: '1px solid rgba(0,0,0,0.08)',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  },
}));

interface ChatMessageEventProps {
  event: ChatMessageEventViewModel;
  roleId?: string;
  orderStatus?: CustomerOrderStatus | HandymanOrderStatus;
  timestamp?: string;
  initiatorName?: string;
  initiatorAvatarUrl?: string | null;
  isInitiatorCustomer?: boolean;
  isInitiatorHandyman?: boolean;
  orderId?: string;
}

const ChatMessageEvent: React.FC<ChatMessageEventProps> = ({
  event,
  roleId,
  orderStatus,
  timestamp,
  initiatorName = 'User',
  initiatorAvatarUrl,
  isInitiatorCustomer = false,
  isInitiatorHandyman = false,
  orderId,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // Format timestamp to show exact date and time
  const formatTimestamp = (ts?: string) => {
    if (!ts) return '';
    try {
      const date = new Date(ts);
      // Format: "MM/DD/YYYY, HH:MM AM/PM" or "DD/MM/YYYY, HH:MM" based on locale
      return date.toLocaleString(undefined, {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return '';
    }
  };

  // Handle click to navigate to order details
  const handleClick = () => {
    if (orderId) {
      navigate(`/orders/${orderId}`);
    }
  };

  // Get the role label for the initiator
  const getInitiatorRoleLabel = () => {
    if (isInitiatorCustomer) return 'Customer';
    if (isInitiatorHandyman) return 'Handyman';
    return 'User';
  };

  // Get initials for avatar
  const getInitiatorInitials = () => {
    return (
      initiatorName
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2) || 'U'
    );
  };

  const getEventIcon = () => {
    switch (event.eventType) {
      /* case MessageEventType.OrderProposalReceived:
        return <LocalOfferIcon sx={{ fontSize: 28 }} color="primary" />; */
      case MessageEventType.OrderCompleted:
        return <CheckCircleIcon sx={{ fontSize: 28 }} color='success' />;
      case MessageEventType.PaymentReceived:
        return <PaymentIcon sx={{ fontSize: 28 }} color='primary' />;
      default:
        return <EventIcon sx={{ fontSize: 28 }} color='action' />;
    }
  };

  const getEventTitle = () => {
    if (event.title) return event.title;

    switch (event.eventType) {
      /* case MessageEventType.OrderProposalReceived:
        return "New Order Proposal"; */
      case MessageEventType.OrderCompleted:
        return 'Order Completed';
      case MessageEventType.PaymentReceived:
        return 'Payment Received';
      default:
        return 'Event';
    }
  };

  const getEventColor = () => {
    switch (event.eventType) {
      /* case MessageEventType.OrderProposalReceived:
        return "rgba(33, 150, 243, 0.04)"; // lighter blue */
      case MessageEventType.OrderCompleted:
        return 'rgba(76, 175, 80, 0.04)'; // lighter green
      case MessageEventType.PaymentReceived:
        return 'rgba(156, 39, 176, 0.04)'; // lighter purple
      default:
        return 'rgba(0, 0, 0, 0.02)'; // very light gray
    }
  };

  const handleAcceptProposal = () => {
    if (event.orderProposalId) {
      dispatch(
        updateHandymanProposal(event.orderProposalId, 'Confirmed') as any,
      );
    }
  };

  const handleRejectProposal = () => {
    if (event.orderProposalId) {
      dispatch(
        updateHandymanProposal(event.orderProposalId, 'Rejected') as any,
      );
    }
  };

  const renderQuickActions = () => {
    if (!roleId) return null;

    switch (event.eventType) {
      /* case MessageEventType.OrderProposalReceived:
        if (roleId === "customer" && orderStatus === OrderStatus.New) {
          return (
            <ButtonGroup size="small" sx={{ mt: 2 }}>
              <Button
                variant="contained"
                color="success"
                startIcon={<DoneIcon />}
                onClick={handleAcceptProposal}
              >
                Accept
              </Button>
              <Button
                variant="contained"
                color="error"
                startIcon={<CancelIcon />}
                onClick={handleRejectProposal}
              >
                Reject
              </Button>
            </ButtonGroup>
          );
        }
        break; */

      case MessageEventType.OrderCompleted:
        if (
          roleId === 'customer' &&
          orderStatus === CustomerOrderStatus.InProgress
        ) {
          return (
            <Button
              variant='contained'
              color='primary'
              startIcon={<CheckCircleIcon />}
              onClick={() => navigate(`/orders/${event.orderId}/complete`)}
              sx={{ mt: 2 }}
            >
              Mark as Complete
            </Button>
          );
        }
        break;
    }

    return null;
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        padding: { xs: '12px', md: '16px' },
        gap: { xs: '12px', md: '16px' },
        width: { xs: '95%', sm: '100%' },
        maxWidth: { xs: '95%', sm: '437px' },
        alignSelf: 'center',
        background: isDark ? '#1A1A1A' : '#E5E5DC',
        boxShadow:
          '0px 1.07796px 2.48379px #000000, 0px 2.07148px 3.72568px rgba(0, 0, 0, 0.4), inset 0px 0.496757px 0.745136px rgba(255, 255, 255, 0.4), inset 0px -0.496757px 0.248379px rgba(0, 0, 0, 0.3), inset 0px 0px 34.773px rgba(0, 0, 0, 0.2)',
        borderRadius: { xs: '24px', md: '32px' },
        marginBottom: { xs: '12px', md: '16px' },
        cursor: orderId ? 'pointer' : 'default',
        transition: 'transform 0.2s ease, box-shadow 0.2s ease',
        '&:hover': orderId
          ? {
              transform: 'translateY(-2px)',
              boxShadow:
                '0px 2px 8px rgba(0, 0, 0, 0.3), 0px 4px 12px rgba(0, 0, 0, 0.4)',
            }
          : {},
      }}
    >
      {/* System Message Card */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          alignItems: 'flex-start',
          padding: { xs: '12px 16px', md: '16px 24px' },
          gap: { xs: '12px', md: '16px' },
          width: '100%',
        }}
      >
        {/* System Label */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '8px',
            width: '100%',
            justifyContent: 'space-between',
          }}
        >
          <Chip
            label='System Message'
            size='small'
            sx={{
              height: '24px',
              fontSize: '11px',
              fontWeight: 600,
              backgroundColor: '#005CAD',
              color: '#FFFFFF',
              '& .MuiChip-label': {
                padding: '0 10px',
                color: '#FFFFFF',
              },
            }}
          />
          <Typography
            sx={{
              fontFamily: "'Montserrat', sans-serif",
              fontWeight: 500,
              fontSize: '12px',
              lineHeight: '166%',
              textAlign: 'right',
              letterSpacing: '0.4px',
              color: isDark ? '#B6B6B6' : '#1A1A1A',
            }}
          >
            {formatTimestamp(timestamp)}
          </Typography>
        </Box>

        {/* Initiator Info */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '12px',
            width: '100%',
          }}
        >
          <Avatar
            src={initiatorAvatarUrl || undefined}
            sx={{
              width: { xs: '40px', md: '48px' },
              height: { xs: '40px', md: '48px' },
              fontSize: { xs: '14px', md: '16px' },
              backgroundColor: '#005CAD',
              color: '#FFFFFF',
              boxShadow:
                '5px 5px 24px -11.7px rgba(0, 0, 0, 0.2), -7.54px -7.54px 8.66px rgba(255, 255, 255, 0.2), 14.15px 14.1509px 22.64px -5.37px rgba(0, 0, 0, 0.1), -2.82453px -2.82453px 8.49057px rgba(255, 255, 255, 0.3)',
            }}
          >
            {getInitiatorInitials()}
          </Avatar>
          <Box>
            <Typography
              sx={{
                fontFamily: "'Roboto', sans-serif",
                fontWeight: 500,
                fontSize: '11px',
                lineHeight: '143%',
                letterSpacing: '0.17px',
                color: isDark ? '#B6B6B6' : '#585858',
                textTransform: 'uppercase',
              }}
            >
              {getInitiatorRoleLabel()}
            </Typography>
            <Typography
              sx={{
                fontFamily: "'Roboto', sans-serif",
                fontWeight: 700,
                fontSize: { xs: '14px', md: '16px' },
                lineHeight: '175%',
                letterSpacing: '0.15px',
                color: isDark ? '#D5D5D5' : '#1A1A1A',
              }}
            >
              {initiatorName}
            </Typography>
          </Box>
        </Box>

        {/* Event Title */}
        {event.title && (
          <Typography
            sx={{
              fontFamily: "'Roboto', sans-serif",
              fontWeight: 600,
              fontSize: '14px',
              lineHeight: '143%',
              letterSpacing: '0.17px',
              color: isDark ? '#D5D5D5' : '#1A1A1A',
              width: '100%',
            }}
          >
            {event.title}
          </Typography>
        )}

        {/* Event Content */}
        <Typography
          sx={{
            fontFamily: "'Roboto', sans-serif",
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '143%',
            letterSpacing: '0.17px',
            color: isDark ? '#B6B6B6' : '#1A1A1A',
            width: '100%',
          }}
        >
          {event.content || getEventTitle()}
        </Typography>

        {/* Quick Actions */}
        {renderQuickActions()}
      </Box>
    </Box>
  );
};

export default ChatMessageEvent;
